import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { isAmxAdmin } from '../utils/auth';
import {
  getAccountPartnerMembership,
  getMembershipDisplayName,
} from '../utils/userProfile';
import AmxGatewayView from '../components/AmxGatewayView';
import GreengrassGatewayMap from '../components/GreengrassGatewayMap';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export default function AmxGatewayManagement(props) {
  const user = useSelector((state) => state.auth.user);
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const admin = isAmxAdmin(user);

  const activeMembership = useMemo(() => {
    return getAccountPartnerMembership(partnerProfile);
  }, [partnerProfile]);

  const partnerId = activeMembership?.partner_id || null;
  const useAdminScope = admin && !partnerId;
  const contextLabel = activeMembership
    ? getMembershipDisplayName(activeMembership)
    : useAdminScope
      ? 'AMX admin scope'
      : 'Unavailable';

  const [gatewayData, setGatewayData] = useState({
    partner_id: partnerId,
    scope_label: contextLabel,
    gateways: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function fetchGatewayInfo() {
      if (!partnerId && !useAdminScope) {
        if (!isMounted) return;

        setGatewayData({
          partner_id: '',
          scope_label: 'Unavailable',
          gateways: [],
        });
        setError(
          user
            ? 'No associated partner or business unit is available for the current user.'
            : ''
        );
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const res = await axiosInstance.get(
          useAdminScope ? '/admin/gateways' : `/partners/${partnerId}/gateways`,
        );
        const apiData = res?.data ?? {};

        if (!isMounted) return;

        setGatewayData({
          partner_id: useAdminScope ? '' : apiData.partner_id || partnerId,
          scope_label: contextLabel,
          gateways: Array.isArray(apiData.gateways) ? apiData.gateways : [],
        });
      } catch (err) {
        console.error('Failed to fetch gateway info', {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
          url: err?.config?.url,
          baseURL: err?.config?.baseURL,
        });

        if (!isMounted) return;

        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to fetch gateway information.'
        );

        setGatewayData({
          partner_id: partnerId,
          scope_label: contextLabel,
          gateways: [],
        });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchGatewayInfo();

    return () => {
      isMounted = false;
    };
  }, [contextLabel, partnerId, useAdminScope, user]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'AMX Gateway Management' },
            ]}
          />

          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Account Context
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5 }}>
                    Gateway Management
                  </Typography>
                </Box>

                <Chip
                  color="primary"
                  variant="outlined"
                  label={useAdminScope
                    ? 'AMX admin scope'
                    : `${gatewayData.scope_label || contextLabel}${gatewayData.partner_id ? ` (${gatewayData.partner_id})` : ''}`}
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            </Paper>

            {!admin && !partnerProfile && user ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 240,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading partner context...</Typography>
              </Box>
            ) : loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 240,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading gateway info...</Typography>
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <>
                <GreengrassGatewayMap data={gatewayData} />
                <AmxGatewayView data={gatewayData} />
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
